import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import { AlertTriangle } from "lucide-react";

import { PrescreenLayout } from "@/components/prescreen";
import { Button } from "@/components/ui/button";
import TermsOfServiceText, {
  TOS_VERSION,
} from "@/components/terms-of-service-text";
import CompanyInformationInfoCardContent from "./company/InformationInfoCardContent";
import CandidateInformationInfoCardContent from "./candidate/InformationInfoCardContent";

import { useDraftProfileStore } from "@/stores/draft-profile";
import { useAcceptTermsOfService } from "@/mutations/user/accept-terms-of-service";
import { useProfileQuery } from "@/queries/user/profile";

const TermsOfServicePrescreen = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect } = useAuth0();

  const { profile } = useProfileQuery();
  const { draftProfileEmail, draftProfileCompanyName } = useDraftProfileStore();
  const {
    acceptTermsOfService,
    isAcceptingTermsOfService,
    acceptTermsOfServiceError,
  } = useAcceptTermsOfService();

  const { nextUrl, previousUrl, InfoContent } = pathname.includes("/company")
    ? {
        nextUrl: "/pre-screen/company/jobs",
        previousUrl: "/pre-screen/company/two",
        InfoContent: CompanyInformationInfoCardContent,
      }
    : {
        nextUrl: "/pre-screen/candidate/three",
        previousUrl: "/pre-screen/candidate/two",
        InfoContent: CandidateInformationInfoCardContent,
      };

  useEffect(() => {
    if (profile?.tosVersionAgreement === TOS_VERSION) {
      navigate(nextUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const onAgree = async () => {
    if (!isAuthenticated) {
      await loginWithRedirect({
        appState: {
          returnTo: nextUrl,
        },
        authorizationParams: {
          login_hint: draftProfileEmail ?? undefined,
          screen_hint: "signup",
          greeting_name: draftProfileCompanyName ?? undefined,
        },
      });
    } else {
      acceptTermsOfService();
    }
  };

  return (
    <PrescreenLayout
      title={"Accept Terms & Conditions"}
      subtitle={
        "By clicking I AGREE, you agree to the Terms & Conditions provided by Venturous, as well as the Terms & Conditions from our partners."
      }
      form={
        <div className="flex-1 flex flex-col gap-8 justify-around self-stretch">
          {acceptTermsOfServiceError && (
            <>
              <div className="w-full flex flex-row items-center gap-4 bg-card border rounded-md p-8">
                <AlertTriangle />
                {acceptTermsOfServiceError.message}
              </div>
            </>
          )}
          {/* <div className="flex flex-col gap-4 self-stretch">
            <h1 className="text-3xl font-bold text-brand-neutral-black">
              Accept Terms & Conditions
            </h1>
            <p className="text-lg text-gray-600">
              By clicking I AGREE, you agree to the Terms & Conditions provided
              by Venturous, as well as the Terms & Conditions from our partners.
            </p>
          </div> */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 h-[500px] overflow-y-auto mb-8 shadow-inner custom-scrollbar">
            <TermsOfServiceText />
          </div>

          <div className="w-full h-[1px] bg-border" />
          <div className="w-full flex flex-row justify-between">
            <Button variant="stroke" onClick={() => navigate(previousUrl)}>
              Back
            </Button>
            <Button
              variant="success"
              disabled={isAcceptingTermsOfService}
              onClick={() => void onAgree()}
            >
              I agree
            </Button>
          </div>
        </div>
      }
      infoCardContent={<InfoContent />}
    />
  );
};

export default TermsOfServicePrescreen;
